import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon } from 'lucide-react';
interface ContextSpotlightProps {
  /** The passage text. The target word/phrase will be located inside this. */
  passage: string;
  /** The exact word or phrase to spotlight. Case-sensitive substring match. */
  targetWord: string;
  /** Optional glossary tooltip / hint shown next to the spotlight. */
  gloss?: string;
  /** Optional source attribution. */
  source?: string;
  /** Optional label rendered above the passage. */
  label?: string;
  /** 'stimulus' = visible while answering, 'support' = post-answer reveal. */
  mode?: 'stimulus' | 'support';
}
/**
 * ContextSpotlight — Vocabulary-in-context visual for woordenschat families.
 *
 * Shows a reading passage with one word/phrase highlighted in a yellow
 * spotlight. Used by TAA-WOORD-CONTEXT, WOORD-IDIOOM and related families
 * where the learner must infer meaning from surrounding context.
 *
 * Differs from readingPassage:
 *   - readingPassage = shows entire passage neutrally for tekstbegrip Qs.
 *   - contextSpotlight = focuses attention on ONE target token; other text
 *     is visually de-emphasised so the learner knows where to look.
 *
 * Seed data:
 * ```json
 * {
 *   "type": "contextSpotlight",
 *   "data": {
 *     "passage": "De jongen was zo blij dat hij in de wolken was.",
 *     "targetWord": "in de wolken",
 *     "gloss": "uitdrukking"
 *   }
 * }
 * ```
 */
export function ContextSpotlight({
  passage,
  targetWord,
  gloss,
  source,
  label,
  mode = 'stimulus'
}: ContextSpotlightProps) {
  const isStimulus = mode === 'stimulus';
  // Locate the target inside the passage. Try case-sensitive first; fall
  // back to case-insensitive so a casing-mismatch in seed data still
  // produces a visible spotlight (and a dev-time warning).
  let idx = passage.indexOf(targetWord);
  let matchLength = targetWord.length;
  if (idx < 0) {
    const lowerIdx = passage.toLowerCase().indexOf(targetWord.toLowerCase());
    if (lowerIdx >= 0) {
      idx = lowerIdx;
      if (typeof console !== 'undefined' && console.warn) {
        console.warn(`[ContextSpotlight] case-insensitive fallback match for "${targetWord}" in passage. Fix the seed casing.`);
      }
    } else if (typeof console !== 'undefined' && console.warn) {
      console.warn(`[ContextSpotlight] targetWord "${targetWord}" not found in passage. Rendering plain text.`);
    }
  }
  const before = idx >= 0 ? passage.slice(0, idx) : passage;
  const match = idx >= 0 ? passage.slice(idx, idx + matchLength) : '';
  const after = idx >= 0 ? passage.slice(idx + matchLength) : '';
  return <motion.div initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.35,
    ease: [0.22, 1, 0.36, 1]
  }} className="w-full" data-id="element-9281">
      {label && <div className="flex items-center gap-2 mb-3" data-id="element-9282">
          <ZapIcon className="w-4 h-4 text-amber-600" data-id="element-9283" />
          <span className="text-sm font-semibold text-amber-800 uppercase tracking-wide" data-id="element-9284">
            {label}
          </span>
        </div>}

      <div className={`relative bg-slate-50 border border-slate-200 rounded-xl ${isStimulus ? 'p-4 sm:p-5' : 'p-5 sm:p-6'}`} data-id="element-9285">
        <div className={`text-slate-500 leading-relaxed whitespace-pre-line ${isStimulus ? 'text-base' : 'text-lg'}`} data-id="element-9286">
          <span data-id="element-9287">{before}</span>
          {match && <motion.span initial={{
          backgroundColor: 'rgba(253, 230, 138, 0.0)'
        }} animate={{
          backgroundColor: 'rgba(253, 230, 138, 1)'
        }} transition={{
          delay: 0.25,
          duration: 0.4
        }} className="inline rounded px-1.5 py-0.5 mx-0.5 font-bold text-slate-900 shadow-sm border border-amber-300" data-id="element-9288">
              {match}
            </motion.span>}
          <span data-id="element-9289">{after}</span>
        </div>

        {gloss && <div className="mt-3 pt-2 border-t border-slate-200 flex items-center gap-2" data-id="element-9290">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-bold" data-id="element-9291">
              i
            </span>
            <span className="text-xs text-slate-600 italic" data-id="element-9292">{gloss}</span>
          </div>}

        {source && <div className="mt-2 text-xs text-slate-400 italic" data-id="element-9293">{source}</div>}
      </div>
    </motion.div>;
}